import React, { useState, useMemo, useEffect } from "react";
import "../../assets/css/--page-faqs.css";
import faqHd from "../../assets/images/Frequently Asked Questions.png";
import { getFaq } from "../../helper/backend-methods";

const faqData2 = {
  "General Questions": [
    {
      question: "Where Is Mains Country Store & Grill Located?",
      answer:
        "Mains Country Store & Grill is located at our central city location, easily accessible and convenient for both dine-in and takeaway customers.",
    },
    {
      question: "What Are Your Operating Hours?",
      answer: "We are open daily from 9:00 AM to 11:00 PM, including weekends and public holidays.",
    },
    {
      question: "Do You Offer Dine-In And Takeout?",
      answer: "Yes, we offer both dine-in and takeout services so you can enjoy our food your way.",
    },
  ],

  "Ordering & Payments": [
    {
      question: "How Can I Place An Order Online?",
      answer: "You can place an order through our official website or mobile app by selecting your items and completing checkout.",
    },
    {
      question: "What Payment Methods Do You Accept?",
      answer: "We accept credit/debit cards, online banking, digital wallets, and cash on delivery.",
    },
    {
      question: "Can I Schedule An Order For Later?",
      answer: "Yes, you can schedule your order for a later date and time during checkout.",
    },
  ],

  "Specials & Offers": [
    {
      question: "Do You Have Daily Or Monthly Specials?",
      answer: "Yes, we regularly offer daily and monthly specials. Check our website or social media pages for the latest deals.",
    },
    {
      question: "Do You Offer Catering Or Bulk Orders?",
      answer: "Yes, we provide catering services and bulk order options for events, parties, and corporate gatherings.",
    },
  ],

  "Account & Tracking": [
    {
      question: "Do I Need An Account To Place An Order?",
      answer: "No, you can place an order as a guest, but creating an account helps you track orders and save preferences.",
    },
    {
      question: "How Do I Track My Order?",
      answer: "Once your order is placed, you can track its status in real time through your account or via order confirmation messages.",
    },
    {
      question: "I Forgot My Password. What Should I Do?",
      answer: "Click on the 'Forgot Password' link on the login page and follow the instructions to reset your password.",
    },
  ],

  Support: [
    {
      question: "How Can I Contact Customer Service?",
      answer: "You can contact our customer service via phone, email, or the contact form available on our website.",
    },
    {
      question: "Do You Accept Feedback Or Suggestions?",
      answer: "Yes, we value your feedback and suggestions. Feel free to share them through our feedback form or social media channels.",
    },
  ],
};

const FaqsPage = () => {
  const [activeCategory, setActiveCategory] = useState("General Questions");
  const [openIndex, setOpenIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchMode, setSearchMode] = useState(false);
  const [faqData, setFaqData] = useState([]);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Function to filter FAQs based on search query
  const filteredFAQs = useMemo(() => {
    if (!searchQuery.trim()) {
      setSearchMode(false);
      return null;
    }

    setSearchMode(true);
    const query = searchQuery.toLowerCase().trim();
    const results = {};

    // Search through all categories
    Object.keys(faqData).forEach((category) => {
      const filteredItems = faqData[category].filter(
        (item) => item.question.toLowerCase().includes(query) || item.answer.toLowerCase().includes(query)
      );

      if (filteredItems.length > 0) {
        results[category] = filteredItems;
      }
    });

    return results;
  }, [searchQuery]);

  // Function to handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    // If search is cleared, reset to normal mode
    if (!value.trim()) {
      setSearchMode(false);
      setOpenIndex(null);
    }
  };

  // Function to clear search
  const clearSearch = () => {
    setSearchQuery("");
    setSearchMode(false);
    setOpenIndex(null);
  };

  // Get current FAQs to display (either filtered or normal)
  const getCurrentFAQs = () => {
    if (searchMode && filteredFAQs) {
      return filteredFAQs;
    }
    return faqData;
  };

  // Get categories to display in the sidebar
  const getDisplayCategories = () => {
    if (searchMode && filteredFAQs) {
      // If no results at all, return empty array
      if (Object.keys(filteredFAQs).length === 0) {
        return [];
      }
      return Object.keys(filteredFAQs);
    }
    return Object.keys(faqData);
  };

  // Get current active category items
  const getActiveCategoryItems = () => {
    const currentFAQs = getCurrentFAQs();

    if (searchMode && filteredFAQs) {
      // In search mode, show search results for the active category
      return filteredFAQs[activeCategory] || [];
    }

    // In normal mode, show all items for the active category
    return currentFAQs[activeCategory] || [];
  };

  // Handle category click
  const handleCategoryClick = (cat) => {
    setActiveCategory(cat);
    setOpenIndex(null);
  };

  // Check if there are any search results at all
  const hasSearchResults = searchMode && filteredFAQs && Object.keys(filteredFAQs).length > 0;

  const getFaqData = () => {
    getFaq()
      .then(({ data }) => {
        if (data.Status) {
          const transformedData = {};
          
          data.Data.forEach((category) => {
            transformedData[category.Title] = category.Detail.map((item) => ({
              question: item.Question,
              answer: item.Answer,
            }));
          });
          
          setFaqData(transformedData);
        
          if (data.Data.length > 0) {
            setActiveCategory(data.Data[0].Title);
          }
        }
      })
      .catch((err) => {
        setFaqData({});
        console.error("Error fetching faqs:", err);
      });
  };
  useEffect(() => {
    getFaqData();
  }, []);
  
  return (
    <section id="FaqsPage">
      <div className="faq-wrapper">
        <div className="faq-hero">
          <h5 className="script">
            <img src={faqHd} alt="Made in Love" className="scriptImg" />
          </h5>
          <h2 className="faqs-hd">GEt your questions answered</h2>
          <p className="faq-para">Find quick answers to the questions we get asked most often or search for topics you're interested in.</p>
        </div>
        <div className="search-inner">
          <input
            type="text"
            placeholder="Search for a question..."
            className="search-bar"
            maxLength={100}
            value={searchQuery}
            onChange={handleSearchChange}
          />
          {searchQuery && (
            <button className="clear-search-btn" onClick={clearSearch} aria-label="Clear search">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="rgba(26,26,26,1)">
                <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-11.414L9.172 7.757 7.757 9.172 10.586 12l-2.829 2.828 1.415 1.415L12 13.414l2.828 2.829 1.415-1.415L13.414 12l2.829-2.828-1.415-1.415L12 10.586z" />
              </svg>
            </button>
          )}
          {!searchQuery && (<span className="faq-search-icon">
            <svg
              className="menu-search-icon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="#5E3A16"
            >
              <path d="M18.031 16.6168L22.3137 20.8995L20.8995 22.3137L16.6168 18.031C15.0769 19.263 13.124 20 11 20C6.032 20 2 15.968 2 11C2 6.032 6.032 2 11 2C15.968 2 20 6.032 20 11C20 13.124 19.263 15.0769 18.031 16.6168ZM16.0247 15.8748C17.2475 14.6146 18 12.8956 18 11C18 7.1325 14.8675 4 11 4C7.1325 4 4 7.1325 4 11C4 14.8675 7.1325 18 11 18C12.8956 18 14.6146 17.2475 15.8748 16.0247L16.0247 15.8748Z"></path>
            </svg>
          </span>)}
        </div>

        {/* Only show no results message when there are NO results at all */}
        {searchMode && (!filteredFAQs || Object.keys(filteredFAQs).length === 0) && (
          <div className="no-results-message">
            <p>No results found. Try different keywords.</p>
          </div>
        )}

        {/* Only show FAQ content when not in search mode OR when there are search results */}
        {(hasSearchResults || !searchMode) && (
          <div className="faq-content">
            <div className="faq-categories">
              {getDisplayCategories().map((cat) => (
                <button key={cat} className={cat === activeCategory ? "active" : ""} onClick={() => handleCategoryClick(cat)}>
                  {cat}
                  {searchMode && filteredFAQs && filteredFAQs[cat] && <span className="result-count">({filteredFAQs[cat].length})</span>}
                </button>
              ))}
            </div>

            <div className="faq-questions">
              <h4 className="tab-heading">
                {searchMode ? `SEARCH RESULTS${activeCategory ? `: ${activeCategory.toUpperCase()}` : ""}` : activeCategory.toUpperCase()}
              </h4>

              {getActiveCategoryItems().map((item, index) => (
                <div key={index} className={`faq-item ${openIndex === index ? "open" : ""}`}>
                  <div className="faq-question" onClick={() => toggleFAQ(index)}>
                    <span className="question">{item.question}</span>
                    <span className="icon">{openIndex === index ? "−" : "+"}</span>
                  </div>

                  <div
                    className="faq-answer"
                    style={{
                      display: openIndex === index ? "block" : "none",
                      whiteSpace: "pre-line",
                    }}
                  >
                    {item.answer}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default FaqsPage;
